import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { getApproval, setApproval, setLicenseVerified, setTaxVerified, verificationsNeeded, type ApprovalState } from "../../api/approval";

/**
 * Edit profile › Approval (superadmin; owner 2026-10-30, self-onboarding).
 * What still stops an operator doing business, the two checks a person makes
 * (PAN/GSTIN genuine, licence document read), and Approve -- which also puts a
 * signed-up operator on AnkhOS Launch with the standard free trial.
 */
export function ApprovalCard({ namespace }: { namespace: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ApprovalState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  const q = useQuery({
    queryKey: ["approval", namespace],
    queryFn: ({ signal }) => getApproval(identity, namespace, signal),
    enabled: !!namespace,
  });
  const state = q.data;
  const blockers = state?.blockers ?? [];
  const need = verificationsNeeded(blockers);

  async function run(action: () => Promise<ApprovalState | void>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await action();
      if (r) setResult(r);
      await qc.invalidateQueries({ queryKey: ["approval", namespace] });
      await qc.invalidateQueries({ queryKey: ["operator", namespace] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not work. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }} data-testid="approval-card">
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" component="h2">Approval</Typography>
          {state && (
            <Chip size="small" color={state.approved ? "success" : "warning"}
              label={state.approved ? "Approved" : "Not approved"} />
          )}
        </Stack>
        {q.isLoading && <Typography variant="body2">Loading…</Typography>}
        {q.isError && <Alert severity="warning">The approval state could not be read.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {result?.plan && <Alert severity="success">Approved. Plan: {result.plan}</Alert>}
        {result?.planerror && <Alert severity="warning">{result.planerror}</Alert>}
        {state && !state.approved && blockers.length > 0 && (
          <Alert severity="info">
            Before approval:
            <ul style={{ margin: "0.4em 0 0", paddingLeft: "1.2em" }}>
              {blockers.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </Alert>
        )}
        {state && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {!state.approved && need.tax && (
              <Button variant="outlined" disabled={busy} onClick={() => void run(() => setTaxVerified(identity, namespace, true))}>
                PAN / GSTIN checked
              </Button>
            )}
            {!state.approved && need.licence && (
              <Button variant="outlined" disabled={busy} onClick={() => void run(() => setLicenseVerified(identity, namespace, true))}>
                Licence document read
              </Button>
            )}
            {!state.approved && (
              <Button variant="contained" disabled={busy || blockers.length > 0}
                onClick={() => void run(() => setApproval(identity, namespace, true))}>
                Approve
              </Button>
            )}
            {state.approved && (
              <Button color="error" disabled={busy} onClick={() => setConfirmWithdraw(true)}>Withdraw approval</Button>
            )}
          </Stack>
        )}
        <Typography variant="body2" color="text.secondary">
          Approving lets this operator appear as a supplier on invoices and, for a new operator, starts AnkhOS Launch with
          the free trial. Only superadmin sees this card.
        </Typography>
      </Stack>
      <Dialog open={confirmWithdraw} onClose={() => setConfirmWithdraw(false)}>
        <DialogTitle>Withdraw approval?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This operator can no longer appear as a supplier on invoices until approved again. Its plan is not changed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmWithdraw(false)}>Keep approval</Button>
          <Button color="error" onClick={() => { setConfirmWithdraw(false); void run(() => setApproval(identity, namespace, false)); }}>
            Withdraw
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
